import React from "react";
import { Link } from "react-router-dom";
import { Row, Col } from "react-flexbox-grid";
import { Button, DialogContainer, SelectField, TextField } from "react-md";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextField as TextField2 } from "@material-ui/core";
import moment from "moment";
import CallButton from "../../../../../../components/common/CallButton/CallButton";
import TopNote from "../../../../../../components/common/TopNote";

// The one-line address the operator sees in the dropdown. Only used for the not-serviceable
// note — the dropdown's own getOptionLabel/renderOption build the same string inline and are
// deliberately left alone, so this change stays out of their render path.
const formatAddress = (address) => {
  if (!address) return "";
  return [
    address.line1,
    address.line2,
    address.landmark,
    address.city,
    address.district,
    address.state,
  ]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(", ")
    .concat(address.pincode ? ` - ${address.pincode}` : "");
};

const UserDetailsSection = (props) => {
  const {
    loading,
    patientIdList,
    setPatientIdList,
    patientDetails,
    setPatientDetails,
    accountList,
    setAccountList,
    accountSelected,
    setAccountSelected,
    planList,
    setPlanList,
    planSelected,
    setPlanSelected,
    // Set when opening the cart could not restore its plan (most often: the plan expired
    // after the cart was raised). Null means "fine, or not asked yet".
    planError = null,
    serviceTypeList,
    setServiceTypeList,
    serviceTypeSelected,
    setServiceTypeSelected,
    serviceListToShow,
    serviceSelected,
    setServiceSelected,
    // Set only when a completed fetch left the group-service field with nothing
    // to select — no active group service on the plan, or the cart's own group
    // service no longer among them. Null means "fine, or not asked yet".
    groupServiceError = null,
    familyMemberList,
    setFamilyMemberList,
    selectedFamilyMember,
    setSelectedFamilyMember,
    // The plan has a wallet but it cannot be spent on the selected family member. Shown
    // right under the dropdown, because the block is a consequence of that choice.
    walletBlockedForMember = false,
    walletBlockedMessage = "",
    relationsList,
    setRelationsList,
    setPatientInfo,
    setUpdatePatientInfoDialog,
    setPatientContactNumber,
    handlePidSearch,
    resetValues,
    confirmUpstreamChange,
    isDOBEmpty,
    isEmailEmpty,
    getAgeFromDob,
    addressList,
    addressLoading = false,
    // A /service/check is in flight for the address the operator just picked.
    addressCheckLoading = false,
    addressSelected,
    setAddressSelected,
    getPatientAddress,
    handleAddressSelectWithValidation,
    // Verdict of a LIVE serviceability check: false renders the note under the field, true and
    // null render nothing. The cart's own addressNotServiceable flag is deliberately NOT
    // surfaced here — it records what was true when the patient raised the request, and the
    // pending-review re-check answers the same question against current coverage.
    addressServiceable = null,
    // The single gate for this whole section: every field here is editable only
    // while the cart is still a DRAFT (status 1) or does not exist yet (CREATE).
    // VERIFY (status 2) and every later/read-only status freeze the cart's
    // identity — Patient ID, account, plan, service type, booking-for and
    // address — so no separate read-only flag is needed here.
    lockIdentityFields = false,
    // The Patient ID is pickable only in the "Add New" (CREATE) flow. Every other mode opens
    // the screen against a specific cart id supplied as a prop, and that prop cannot be reset
    // — so re-pointing the patient there would leave the screen writing to the previous
    // patient's cart. Re-picking the patient on a cart you opened *by id* is meaningless
    // anyway: a cart belongs to exactly one patient.
    canPickPatient = false,
  } = props;

  const patientFieldLocked = lockIdentityFields || !canPickPatient;

  return (
    <div>
      <div>
        <h3>{`User details`}</h3>
        <Row>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Patient ID`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <Autocomplete
                  style={{ top: "2px" }}
                  id="appointment_patient_id"
                  options={patientIdList}
                  value={patientDetails}
                  disabled={patientFieldLocked}
                  onChange={(e, v) => {
                    if (v) {
                      // A patient can only be picked into an EMPTY field: swapping one patient
                      // straight for another would set the new patient without ever running
                      // resetValues(1), leaving the previous patient's documents and cart id
                      // behind — and those get written back under the new patient. Clearing is
                      // the only way to change patient, and clearing is what resets the screen.
                      // Unreachable today (the PID search yields a single exact match, so the
                      // only option offered IS the selected patient), but this is the invariant
                      // the reset depends on, so it is enforced rather than assumed.
                      if (patientDetails) return;
                      if (isDOBEmpty(v) || isEmailEmpty(v)) {
                        setPatientInfo(v);
                        setUpdatePatientInfoDialog(true);
                      } else {
                        setPatientDetails(v);
                        setPatientContactNumber(v.phoneno);
                      }
                    } else {
                      resetValues(1);
                    }
                  }}
                  getOptionLabel={(option) =>
                    patientDetails
                      ? `${option.id}`
                      : `(${option.id}) ${option.firstName} ${option.lastName}`
                  }
                  filterOptions={(x) => x}
                  onInputChange={(e, value, reason) => {
                    if (!patientDetails && /^\d*$/.test(value)) {
                      handlePidSearch(e, value, reason);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField2
                      {...params}
                      placeholder="Type here"
                      inputProps={{
                        ...params.inputProps,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        readOnly: !!patientDetails,
                        onKeyPress: (e) => {
                          if (patientDetails) {
                            e.preventDefault();
                          } else if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontFamily: "Montserrat, sans-serif",
                          marginTop: "8px",
                        },
                        classes: {
                          underline: "custom-underline",
                        },
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </Col>
        </Row>

        <br />
        <Row>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Patient name`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <TextField
                  value={
                    patientDetails
                      ? `${patientDetails.firstName} ${patientDetails.lastName}`
                      : ""
                  }
                  lineDirection="left"
                  disabled
                />
              </div>
            </div>
          </Col>
          <Col xs={12} sm={1} md={1} lg={1}>
            <div style={{ height: "20px" }}></div>
          </Col>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Patient number`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "40px", marginRight: "20px" }}>
                    <TextField
                      value={
                        patientDetails ? `+${patientDetails.countryCode}` : ""
                      }
                      lineDirection="left"
                      disabled
                    />
                  </div>
                  <div style={{ flex: "1" }}>
                    <TextField
                      value={patientDetails ? `${patientDetails.phoneno}` : ""}
                      lineDirection="left"
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={12} sm={1} md={1} lg={1}>
            <div style={{ height: "20px" }}></div>
          </Col>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Patient current age`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <TextField
                  value={
                    patientDetails
                      ? `${getAgeFromDob(patientDetails?.dateOfBirth)}`
                      : ""
                  }
                  lineDirection="left"
                  disabled
                />
              </div>
            </div>
          </Col>
        </Row>

        <br />
        <Row>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Date of birth`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <TextField
                  value={
                    patientDetails
                      ? `${moment(patientDetails.dateOfBirth).format(
                          "DD MMM YYYY",
                        )}`
                      : ""
                  }
                  lineDirection="left"
                  disabled
                />
              </div>
            </div>
          </Col>
          <Col xs={12} sm={1} md={1} lg={1}>
            <div style={{ height: "20px" }}></div>
          </Col>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Email ID`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <TextField
                  value={
                    patientDetails
                      ? `${patientDetails.email ? patientDetails.email : ""}`
                      : ""
                  }
                  lineDirection="left"
                  disabled
                />
              </div>
            </div>
          </Col>
          <Col xs={12} sm={1} md={1} lg={1}>
            <div style={{ height: "20px" }}></div>
          </Col>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Gender`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <TextField
                  value={
                    patientDetails
                      ? `${
                          patientDetails.gender === 1
                            ? "Female"
                            : patientDetails.gender === 2
                              ? "Male"
                              : ""
                        }`
                      : ""
                  }
                  lineDirection="left"
                  disabled
                />
              </div>
            </div>
          </Col>
        </Row>

        <br />
        <Row>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Select account`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <Autocomplete
                  id="account_drop_down"
                  options={accountList}
                  value={accountSelected}
                  disabled={loading || lockIdentityFields}
                  onChange={async (e, v) => {
                    // account → plan → documents: prescriptions are cleared too.
                    if (!(await confirmUpstreamChange(true, true))) return;
                    if (v) {
                      if (accountSelected != null) {
                        resetValues(2);
                      }
                      setAccountSelected(v);
                    } else {
                      setAccountSelected(null);
                      resetValues(2);
                    }
                  }}
                  getOptionLabel={(option) => option || ""}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField2
                      {...params}
                      placeholder="Select"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontFamily: "Montserrat, sans-serif",
                          marginTop: "8px",
                        },
                        classes: {
                          underline: "custom-underline",
                        },
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </Col>
        </Row>
        <br />
        <Row>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Select plan`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <Autocomplete
                  id="plan_drop_down"
                  options={planList}
                  value={planSelected}
                  disabled={loading || lockIdentityFields}
                  onChange={async (e, v) => {
                    // plan → documents: prescriptions are cleared too.
                    if (!(await confirmUpstreamChange(true, true))) return;
                    if (v) {
                      if (planSelected != null) {
                        resetValues(3);
                      }
                      setPlanSelected(v);
                    } else {
                      setPlanSelected(null);

                      resetValues(3);
                    }
                  }}
                  getOptionLabel={(option) =>
                    option ? `(${option.planId}) ${option.name}` : ""
                  }
                  filterOptions={(options, { inputValue }) => {
                    const searchText = inputValue.toLowerCase();

                    return options.filter((option) => {
                      const planName = option.name.toLowerCase();
                      const planId = option.planId.toString();

                      return (
                        planName.includes(searchText) ||
                        planId.includes(searchText)
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField2
                      {...params}
                      placeholder="Select"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontFamily: "Montserrat, sans-serif",
                          marginTop: "8px",
                        },
                        classes: {
                          underline: "custom-underline",
                        },
                      }}
                    />
                  )}
                />
                {planError && (
                  <div style={{ marginTop: "8px" }}>
                    <TopNote
                      text={planError}
                      color="#FBE3E1"
                      borderColor="#E44437"
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <br />
        <Row>
          <Col xs={12} sm={3} md={3} lg={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Select service type`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <Autocomplete
                  id="mp-cart-service-type-selection"
                  options={serviceTypeList}
                  value={serviceTypeSelected}
                  disabled={loading || lockIdentityFields}
                  onChange={async (e, v) => {
                    if (!(await confirmUpstreamChange(true))) return;
                    if (v) {
                      if (serviceTypeSelected != null) {
                        resetValues(4);
                      }
                      setServiceTypeSelected(v);
                    } else {
                      setServiceTypeSelected(null);

                      resetValues(4);
                    }
                  }}
                  getOptionLabel={(option) =>
                    option ? option.serviceTypeName : ""
                  }
                  filterOptions={(options, { inputValue }) => {
                    const searchText = inputValue.toLowerCase();

                    return options.filter((option) => {
                      const serviceTypeName =
                        option.serviceTypeName?.toLowerCase() || "";

                      return serviceTypeName.includes(searchText);
                    });
                  }}
                  renderInput={(params) => (
                    <TextField2
                      {...params}
                      placeholder="Select"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontFamily: "Montserrat, sans-serif",
                          marginTop: "8px",
                        },
                        classes: {
                          underline: "custom-underline",
                        },
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </Col>
        </Row>
        <br />
        <Row>
          <Col xs={12} sm={5} md={5} lg={5}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Booking for`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <Autocomplete
                  id="service_type_dropdown"
                  options={familyMemberList}
                  disabled={
                    loading || serviceTypeSelected == null || lockIdentityFields
                  }
                  value={selectedFamilyMember}
                  onChange={async (e, v) => {
                    // clearsDocuments: a family booking files one copy of each prescription
                    // UNDER THE MEMBER, so re-pointing the cart orphans it and the backend
                    // clears both document lists (bookedFor -> documents). Warn by name — the
                    // copy already filed under the previous member cannot be taken back
                    // (document-service has no delete route), so this is not undo-able.
                    if (!(await confirmUpstreamChange(false, true))) return;
                    if (v) {
                      if (selectedFamilyMember != null) {
                        resetValues(9);
                      }
                      setSelectedFamilyMember(v);
                    } else {
                      setSelectedFamilyMember(null);
                      resetValues(9);
                    }
                  }}
                  getOptionLabel={(option) =>
                    option
                      ? `(${option.memberPatientId}) ${option.firstName} ${
                          option.lastName
                        } (${
                          option?.gender === 1
                            ? "F"
                            : option?.gender === 2
                              ? "M"
                              : "-"
                        }/${option?.dateOfBirth}/${
                          relationsList.find((e) => e.code === option.relation)
                            .value
                        })`
                      : ""
                  }
                  filterOptions={(options, { inputValue }) => {
                    const searchText = inputValue.toLowerCase();

                    return options.filter((option) => {
                      const memberPatientId =
                        option.memberPatientId?.toString() || "";
                      const firstName = option.firstName?.toLowerCase() || "";
                      const lastName = option.lastName?.toLowerCase() || "";
                      const relationValue =
                        relationsList
                          .find((e) => e.code === option.relation)
                          ?.value?.toLowerCase() || "";

                      return (
                        memberPatientId.includes(searchText) ||
                        firstName.includes(searchText) ||
                        lastName.includes(searchText) ||
                        relationValue.includes(searchText)
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField2
                      {...params}
                      placeholder="Select"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontFamily: "Montserrat, sans-serif",
                          marginTop: "8px",
                        },
                        classes: {
                          underline: "custom-underline",
                        },
                      }}
                    />
                  )}
                />
              </div>
              {walletBlockedForMember && (
                <div className="cdv-cart-wallet-blocked-notice">
                  {walletBlockedMessage ||
                    "Zyla wallet can't be used for this family member."}
                </div>
              )}
            </div>
          </Col>
          <Col xs={12} sm={1} md={1} lg={1}>
            <div style={{ height: "20px" }}></div>
          </Col>
          <Col xs={12} sm={1} md={1} lg={1}>
            {selectedFamilyMember && patientDetails && (
              <div style={{ paddingTop: "20px" }}>
                <CallButton
                  patient={{
                    id: selectedFamilyMember.memberPatientId,
                    phoneno: patientDetails.phoneno,
                    countryCode: patientDetails.countryCode,
                  }}
                />
              </div>
            )}
          </Col>
        </Row>
        <br />
        <Row>
          <Col xs={12} sm={5} md={5} lg={5}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Select group service`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <Autocomplete
                  id="cart-group-service-selection"
                  options={serviceListToShow || []}
                  value={serviceSelected}
                  disabled
                  onChange={(e, v) => {
                    setServiceSelected(v || null);
                  }}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    const serviceId = option.serviceId ?? "";
                    const name = option.name ?? option.serviceName ?? "";
                    return `(${serviceId}) ${name}`;
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const searchText = inputValue.toLowerCase();
                    return options.filter((option) => {
                      const name = (
                        option.name ||
                        option.serviceName ||
                        ""
                      ).toLowerCase();
                      const serviceId = (option.serviceId ?? "").toString();
                      return (
                        name.includes(searchText) ||
                        serviceId.includes(searchText)
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField2
                      {...params}
                      placeholder="Select"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontFamily: "Montserrat, sans-serif",
                          marginTop: "8px",
                        },
                        classes: {
                          underline: "custom-underline",
                        },
                      }}
                    />
                  )}
                />
                {/* The field is disabled and auto-filled, so an empty one looks exactly like
                    one that has not loaded yet. This is the only thing that tells the
                    operator the cart cannot be booked and why. */}
                {groupServiceError && (
                  <div style={{ marginTop: "8px" }}>
                    <TopNote
                      text={groupServiceError}
                      color="#FBE3E1"
                      borderColor="#E44437"
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <br />
        <Row>
          <Col xs={12} sm={11} md={11} lg={11}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">
                {`Select address`}
                <label className="cart-module-label-red"> *</label>
              </div>
              <div>
                <Autocomplete
                  id="cart-address-selection"
                  options={addressList}
                  value={addressSelected}
                  loading={addressLoading}
                  loadingText="Loading addresses…"
                  // Each open re-fetches, so the selected address and its counterpart in the
                  // refreshed list are equal-but-distinct objects. Without an id-based test the
                  // default reference equality never matches and the selection stops
                  // highlighting (and MUI warns the value is invalid).
                  getOptionSelected={(option, value) =>
                    option?.id === value?.id
                  }
                  // Locked while a check is in flight: the vendor-manager fans out serially
                  // over every cart-enabled vendor, so this takes long enough that an open
                  // dropdown invites a second pick on top of the first.
                  disabled={
                    loading ||
                    selectedFamilyMember == null ||
                    lockIdentityFields ||
                    addressCheckLoading
                  }
                  onOpen={() => {
                    getPatientAddress();
                  }}
                  onChange={(e, v) => {
                    if (v) {
                      handleAddressSelectWithValidation(v);
                    } else {
                      setAddressSelected(null);
                    }
                  }}
                  getOptionLabel={(option) =>
                    option
                      ? `${option.line1}, ${option.line2}, ${
                          option.landmark && option.landmark != ""
                            ? `${option.landmark}, `
                            : ""
                        }${option.city}, ${option.district}, ${
                          option.state
                        } - ${option.pincode}`
                      : ""
                  }
                  filterOptions={(options, { inputValue }) => {
                    const searchText = inputValue.toLowerCase();

                    return options.filter((option) => {
                      const addressString = [
                        option.line1,
                        option.line2,
                        option.landmark,
                        option.city,
                        option.district,
                        option.state,
                        option.pincode,
                      ]
                        .filter(Boolean)
                        .join(", ")
                        .toLowerCase();

                      return addressString.includes(searchText);
                    });
                  }}
                  renderOption={(option) => {
                    const hasValidCoordinates =
                      option?.coordinates &&
                      option.coordinates.latitude !== 0 &&
                      option.coordinates.longitude !== 0;

                    const label = option
                      ? `${option.line1}, ${option.line2}, ${
                          option.landmark && option.landmark !== ""
                            ? `${option.landmark}, `
                            : ""
                        }${option.city}, ${option.district}, ${option.state} - ${option.pincode}`
                      : "";

                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {!hasValidCoordinates && (
                          <span
                            style={{
                              marginRight: 8,
                              padding: "2px 8px",
                              fontSize: 12,
                              borderRadius: 12,
                              backgroundColor: "#E9E7FE",
                              color: "#6A5FF5",
                              fontWeight: 600,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            Update
                          </span>
                        )}

                        <span>{label}</span>
                      </div>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField2
                      {...params}
                      placeholder="Select"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontFamily: "Montserrat, sans-serif",
                          marginTop: "8px",
                        },
                        classes: {
                          underline: "custom-underline",
                        },
                      }}
                    />
                  )}
                />
                {addressCheckLoading && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      color: "#6A5FF5",
                      fontWeight: 600,
                    }}
                  >
                    Checking serviceability for this address…
                  </div>
                )}
                {/* Sits UNDER the field, not above it: the note names the address that failed,
                    so it reads as a verdict on the row directly above rather than a warning
                    about a choice the operator has yet to make. */}
                {addressServiceable === false && (
                  <div style={{ marginTop: "8px" }}>
                    <TopNote
                      text={`Not serviceable: This address is not serviceable (${formatAddress(
                        addressSelected,
                      )}). Service provider selection will be enabled after a lab is assigned.`}
                      color="#FBE3E1"
                      borderColor="#E44437"
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} sm={1} md={1} lg={1}>
            {/* Mirrors the dropdown's own gate: once the address field is frozen a newly
                added address could never be selected onto the cart, so the shortcut goes
                with it. */}
            {!(
              loading ||
              selectedFamilyMember == null ||
              lockIdentityFields
            ) && (
              <Link
                // The list is scoped to the booking-for member, so a new address has to be
                // added against that member — adding it to the account holder would leave
                // the operator staring at a dropdown that never shows what they just created.
                //
                // Profile-v2's Addresses section, with its add form already open:
                // "?address=true" is the legacy screen's own flag, answered there now
                // (profile-v2/index.js _openAddressFromUrl). A NEW TAB is kept for this
                // one alone — the operator is mid-booking, and taking the cart off the
                // screen to add an address would lose what they have typed
                // (Nittin, 2026-09-25).
                to={`/profile-v2/${selectedFamilyMember?.memberPatientId}?address=true`}
                target="_blank"
                className="cart-module-add-more"
                style={{ height: "20px" }}
              >
                + Add new
              </Link>
            )}
          </Col>
        </Row>
        <br />
      </div>
    </div>
  );
};

export default UserDetailsSection;
